import { useEffect, useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { DISMISS_REASONS, dismissDiscrepancy } from "@/api/discrepancies";
import { Modal } from "@/components/ui/Modal";
import type { ApiError } from "@/types";
import type { DismissReason } from "@/types/ezyvet";

interface Props {
  discrepancy: { id: string; event_description: string };
  open: boolean;
  onClose: () => void;
  onDismissed: () => void;
}

/** Marks a discrepancy as intended or a false positive. A reason code is required; "Other" also needs a note. */
export function DismissModal({ discrepancy, open, onClose, onDismissed }: Props) {
  const [reason, setReason] = useState<DismissReason | null>(null);
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setReason(null);
      setNote("");
      setSubmitted(false);
    }
  }, [open]);

  const dismiss = useMutation<void, ApiError, { reason: DismissReason; note: string }>({
    mutationFn: ({ reason, note }) => dismissDiscrepancy(discrepancy.id, reason, note),
    onSuccess: () => {
      onDismissed();
      onClose();
    },
  });
  const { reset } = dismiss;
  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const noteRequired = reason === "other";
  const problem = !reason ? "Choose a reason." : noteRequired && !note.trim() ? "Describe the reason." : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!problem && reason && !dismiss.isPending) dismiss.mutate({ reason, note });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Dismiss discrepancy"
      footer={
        <>
          <button type="button" className="btn btn-sm" onClick={onClose} disabled={dismiss.isPending}>
            Cancel
          </button>
          <button type="submit" form="dismiss-form" className="btn btn-sm btn-danger" disabled={dismiss.isPending}>
            {dismiss.isPending ? "Dismissing…" : "Dismiss"}
          </button>
        </>
      }
    >
      <form id="dismiss-form" onSubmit={submit} noValidate>
        <p className="muted">
          <b style={{ color: "var(--text)" }}>{discrepancy.event_description}</b> won&apos;t count toward leakage. The reason
          is kept with the audit.
        </p>
        <fieldset className="radio-group" aria-required="true" aria-invalid={submitted && !reason}>
          <legend>Reason</legend>
          {(Object.entries(DISMISS_REASONS) as [DismissReason, string][]).map(([code, label], i) => (
            <label key={code} className="radio-row">
              <input
                type="radio"
                name="dismiss-reason"
                value={code}
                checked={reason === code}
                onChange={() => setReason(code)}
                {...(i === 0 ? { "data-autofocus": true } : {})}
              />
              <span>{label}</span>
            </label>
          ))}
        </fieldset>
        <div className="field">
          <label htmlFor="dismiss-note">Note{noteRequired ? "" : " (optional)"}</label>
          <textarea
            id="dismiss-note"
            style={{ minHeight: 80, fontFamily: "inherit" }}
            maxLength={450}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-required={noteRequired}
          />
        </div>
        {submitted && problem && (
          <span className="field-error" role="alert">
            {problem}
          </span>
        )}
        {dismiss.error && (
          <div className="callout error" role="alert">
            Couldn&apos;t dismiss: {dismiss.error.message}
          </div>
        )}
      </form>
    </Modal>
  );
}
